import React, { FC, useEffect, useState } from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import { snowflakeConnectionPrivateKeyIdSelector } from "@/store/provider-connections/selectors/snowflake/snowflakeConnectionPrivateKeyIdSelector";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { enqueueSnackbarErrorAlert } from "@/components/pages/common/snackbar-alert/EnqueueSnackbarErrorAlert";
import {
  useGetUsersMeProviderConnectionsSnowflakeQuery,
  usePostUsersMeProviderConnectionsSnowflakeMutation,
} from "@/services/cloudchipr.api";
import { SnowflakeAccountIdentifierField } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/fields/SnowflakeAccountIdentifierField";
import { SnowflakeServiceAccountField } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/fields/SnowflakeServiceAccountField";
import { setActiveProviderConnectionIsCreated } from "@/store/provider-connections/providerConnectionsSlice";
import { activeProviderConnectionIsCreatedSelector } from "@/store/provider-connections/selectors/active-connection/activeProviderConnectionIsCreatedSelector";

export const SnowflakeConnectionForm: FC = () => {
  const dispatch = useAppDispatch();
  const privateKeyId = useAppSelector(snowflakeConnectionPrivateKeyIdSelector);
  const [createConnection, { isLoading, error }] =
    usePostUsersMeProviderConnectionsSnowflakeMutation();
  const isConnectionCreated = useAppSelector(
    activeProviderConnectionIsCreatedSelector,
  );
  const { refetch } = useGetUsersMeProviderConnectionsSnowflakeQuery();

  const [isConnected, setIsConnected] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      account_identifier: "",
      service_account: "",
    },
    validationSchema: yup.object({
      name: yup.string().required(),
      account_identifier: yup.string().required(),
      service_account: yup.string().required(),
    }),
    onSubmit: async ({ name, account_identifier, service_account }) => {
      try {
        if (privateKeyId) {
          await createConnection({
            body: {
              name,
              account_identifier,
              service_account,
              private_key_id: privateKeyId,
            },
          });
          setIsConnected(true);
          refetch();
        }
      } catch {
        // @ts-expect-error todo: fix api spec;
        enqueueSnackbarErrorAlert(e?.data?.message);
      }
    },
  });

  useEffect(() => {
    if (isConnected && !error && !isLoading) {
      dispatch(setActiveProviderConnectionIsCreated(true));
    }
  }, [isConnected, error, isLoading, dispatch]);

  const { dirty, isValid, values, errors, handleChange, touched, submitForm } =
    formik;

  return (
    <Stack py={2} spacing={1.5}>
      <Typography fontWeight="medium">Provide Details</Typography>
      <Box>
        <Typography>Account Name</Typography>
        <TextField
          fullWidth
          value={values.name ?? ""}
          type="text"
          size="small"
          name="name"
          error={!!(touched.name && errors.name)}
          helperText="Choose a friendly name to identify your Snowflake account across the platform."
          onChange={handleChange}
        />
      </Box>

      <SnowflakeAccountIdentifierField
        accountIdentifier={values.account_identifier}
        handleChange={handleChange}
        error={!!(touched.account_identifier && errors.account_identifier)}
      />

      <SnowflakeServiceAccountField
        serviceAccount={values.service_account}
        handleChange={handleChange}
        error={!!(touched.service_account && errors.service_account)}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        p={1}
        borderRadius={2}
        bgcolor="grey.100"
      >
        <Typography pl={1}>
          Provide details and check connection to proceed.
        </Typography>
        <LoadingButton
          type="submit"
          disabled={!(isValid && dirty)}
          color="primary"
          variant="contained"
          onClick={submitForm}
          loading={isLoading}
          loadingPosition="start"
          sx={{ pl: isLoading ? 5 : 2 }}
        >
          Check Connection
        </LoadingButton>
      </Stack>
      {error && (
        <Alert variant="outlined" severity="warning">
          Connection failed. Please make sure all details are correct and try
          again.
        </Alert>
      )}
      {isConnectionCreated && !error && (
        <Alert variant="outlined" severity="success">
          Successfully Connected.
        </Alert>
      )}
    </Stack>
  );
};
